export const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Legacy routes return { error: 'msg' }; refactored modules return
// { error: { code, message, details? } }. This helper accepts both.
const extractErrorMessage = (data) => {
  if (!data) return 'Request failed';
  if (typeof data.error === 'string') return data.error;
  if (data.error && typeof data.error.message === 'string') return data.error.message;
  return 'Request failed';
};

// Signals to App.js that the current session is invalid. App.js listens for
// this and resets auth state — we intentionally do NOT call location.reload()
// here because an unauthenticated page making API calls at startup could
// reload-loop forever.
let authExpiredNotified = false;
const notifyAuthExpired = () => {
  if (authExpiredNotified) return;
  authExpiredNotified = true;
  localStorage.removeItem('hrms_token');
  localStorage.removeItem('hrms_refresh_token');
  window.dispatchEvent(new Event('hrms-auth-expired'));
};

// A 401 means the access token expired — try to silently mint a new one
// from the refresh token before giving up and logging the user out. This
// is what stops "logged out after a few hours" for anyone who still has
// a valid (<=30 day) refresh token: concurrent 401s share one in-flight
// refresh instead of each firing their own /auth/refresh call.
let refreshPromise = null;
const refreshAccessToken = () => {
  if (refreshPromise) return refreshPromise;
  const refreshToken = localStorage.getItem('hrms_refresh_token');
  if (!refreshToken) return Promise.resolve(false);
  refreshPromise = fetch(`${BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
    .then(async (res) => {
      // Another tab may have rotated the token first — if localStorage now
      // holds a different refresh token, that tab already won; just retry.
      if (!res.ok) return localStorage.getItem('hrms_refresh_token') !== refreshToken && !!localStorage.getItem('hrms_token');
      const data = await res.json().catch(() => null);
      if (!data?.token) return false;
      localStorage.setItem('hrms_token', data.token);
      if (data.refresh_token) localStorage.setItem('hrms_refresh_token', data.refresh_token);
      return true;
    })
    .catch(() => false)
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
};

const api = {
  getToken: () => localStorage.getItem('hrms_token'),

  async request(method, path, body, _retried) {
    const token = this.getToken();
    const opts = {
      method,
      headers: { 'Content-Type': 'application/json' },
    };
    if (token) opts.headers['Authorization'] = `Bearer ${token}`;
    if (body) opts.body = JSON.stringify(body);
    console.log(`[api] → ${method} ${path}`, { hasToken: !!token });
    const res = await fetch(`${BASE}${path}`, opts);
    console.log(`[api] ← ${method} ${path} status=${res.status}`);
    if (res.status === 401) {
      if (!_retried && (await refreshAccessToken())) {
        return this.request(method, path, body, true);
      }
      console.log(`[api] 401 received — wiping token and firing hrms-auth-expired`);
      notifyAuthExpired();
      return null;
    }
    // If the response isn't JSON (e.g., the API is down and nginx served
    // an HTML error page) parsing it throws a cryptic "Unexpected token
    // '<'" message that hides what actually happened. Sniff the content
    // type first and turn HTML into a plain-English hint.
    const ct = (res.headers.get('content-type') || '').toLowerCase();
    if (!ct.includes('application/json')) {
      const text = await res.text().catch(() => '');
      const looksLikeHtml = /^\s*<(!doctype|html|head|body)/i.test(text);
      const hint = looksLikeHtml
        ? `Server returned an HTML page instead of JSON (status ${res.status}). The API is likely down or the reverse proxy isn't forwarding ${path} to the backend.`
        : `Server returned ${ct || 'unknown content type'} instead of JSON (status ${res.status}).`;
      throw new Error(hint);
    }
    const data = await res.json();
    if (!res.ok) {
      const e = new Error(extractErrorMessage(data));
      // Preserve the raw error body — some endpoints attach extra flags
      // (e.g. { needsMoreInput: true }) callers need beyond the message.
      e.data = data;
      e.status = res.status;
      throw e;
    }
    return data;
  },

  get(path) { return this.request('GET', path); },
  post(path, body) { return this.request('POST', path, body); },
  put(path, body) { return this.request('PUT', path, body); },
  patch(path, body) { return this.request('PATCH', path, body); },
  del(path, body) { return this.request('DELETE', path, body); },

  // Authenticated file download (CSV / Excel reports). A plain <a href> cannot
  // send the Authorization header, so fetch the bytes and hand the browser a blob.
  async download(path, fallbackName = 'download', _retried) {
    const token = this.getToken();
    const res = await fetch(`${BASE}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (res.status === 401) {
      if (!_retried && (await refreshAccessToken())) return this.download(path, fallbackName, true);
      notifyAuthExpired();
      return null;
    }
    if (!res.ok) {
      let data = null;
      try { data = await res.json(); } catch (_) { data = null; }
      throw new Error(extractErrorMessage(data) || 'Download failed');
    }
    const cd = res.headers.get('content-disposition') || '';
    const m = /filename="?([^";]+)"?/i.exec(cd);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = m ? m[1] : fallbackName;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  },

  async upload(path, formData, method = 'POST', _retried) {
    const token = this.getToken();
    const opts = { method, body: formData };
    if (token) opts.headers = { 'Authorization': `Bearer ${token}` };
    const res = await fetch(`${BASE}${path}`, opts);
    if (res.status === 401) {
      if (!_retried && (await refreshAccessToken())) {
        return this.upload(path, formData, method, true);
      }
      notifyAuthExpired();
      return null;
    }
    let data = null;
    try { data = await res.json(); } catch (_) { data = {}; }
    if (!res.ok) throw new Error(extractErrorMessage(data) || 'Upload failed');
    return data;
  },

  // Same job as upload() but reports progress (0-1) as the body streams out
  // — plain fetch() has no upload-progress hook, so this goes through
  // XMLHttpRequest instead just for this one case. Used by the chat
  // attachment senders to drive a WhatsApp-style upload ring on large files.
  uploadWithProgress(path, formData, onProgress, method = 'POST', _retried) {
    const token = this.getToken();
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, `${BASE}${path}`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (onProgress && e.lengthComputable) onProgress(e.loaded / e.total);
      };
      xhr.onload = async () => {
        if (xhr.status === 401) {
          if (!_retried && (await refreshAccessToken())) {
            resolve(this.uploadWithProgress(path, formData, onProgress, method, true));
            return;
          }
          notifyAuthExpired();
          resolve(null);
          return;
        }
        let data = null;
        try { data = JSON.parse(xhr.responseText); } catch (_) { data = {}; }
        if (xhr.status < 200 || xhr.status >= 300) {
          reject(new Error(extractErrorMessage(data) || 'Upload failed'));
          return;
        }
        resolve(data);
      };
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(formData);
    });
  },

  // NOTE: login intentionally does NOT go through `request`. The generic
  // request handler treats any 401 as "session expired" — it wipes the
  // token, fires hrms-auth-expired, and returns null. On the login page
  // there's no session yet, and a 401 means "wrong credentials", not
  // "your session died". Sending it through that path made
  // `data.token` throw "Cannot read properties of null (reading 'token')"
  // and that developer-y string reached the user as their login error.
  //
  // We handle fetch here directly, distinguish the actual failure modes
  // (bad creds / rate limit / missing fields / network / server), and
  // surface machine-readable codes so the login page can render a
  // human-friendly message with the remaining-attempts hint.
  async login(username, password) {
    let res;
    try {
      res = await fetch(`${BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
    } catch (_) {
      const err = new Error('Network unavailable');
      err.code = 'NETWORK';
      throw err;
    }

    // express-rate-limit exposes these via standard headers.
    // RateLimit-Remaining = how many attempts left in the current window.
    // Retry-After (on 429) = seconds until the window resets.
    const remainingHdr = res.headers.get('RateLimit-Remaining') || res.headers.get('X-RateLimit-Remaining');
    const retryAfterSec = parseInt(res.headers.get('Retry-After') || '0', 10);

    let data = null;
    try { data = await res.json(); } catch (_) {}

    if (res.status === 429) {
      const err = new Error('Locked');
      err.code = 'LOCKED';
      err.retryAfterMin = retryAfterSec ? Math.max(1, Math.ceil(retryAfterSec / 60)) : 15;
      throw err;
    }
    if (res.status === 401) {
      const err = new Error('Bad credentials');
      err.code = 'BAD_CREDENTIALS';
      err.remaining = remainingHdr != null ? parseInt(remainingHdr, 10) : null;
      throw err;
    }
    if (res.status === 400) {
      const err = new Error('Missing fields');
      err.code = 'MISSING_FIELDS';
      throw err;
    }
    if (res.status >= 500) {
      const err = new Error('Server error');
      err.code = 'SERVER';
      throw err;
    }
    if (!res.ok || !data?.token) {
      const err = new Error(extractErrorMessage(data) || 'Login failed');
      err.code = 'UNKNOWN';
      throw err;
    }

    localStorage.setItem('hrms_token', data.token);
    if (data.refresh_token) localStorage.setItem('hrms_refresh_token', data.refresh_token);
    authExpiredNotified = false;
    return data;
  },

  logout() {
    const refreshToken = localStorage.getItem('hrms_refresh_token');
    // Fire-and-forget: revoke server-side so the refresh token can't be
    // replayed after this device signs out, but don't block local logout
    // on it (the server may be unreachable).
    if (refreshToken) {
      fetch(`${BASE}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      }).catch(() => {});
    }
    localStorage.removeItem('hrms_token');
    localStorage.removeItem('hrms_refresh_token');
    window.dispatchEvent(new Event('hrms-auth-expired'));
  }
};

export default api;
